// Contacts & portal access — on the client record. docs/CLIENT_PORTAL.md §4.
//
// The people at a client, and which of them may sign in to the client portal
// to see and pay this client's invoices. Nobody gets in by asking: a contact
// starts with the portal OFF, and "Invite" is what turns it on (and emails a
// sign-in link). "Turn off portal access" ends it on that person's next
// request; when this was their last client, every device they had signed in
// is signed out at once (server/routes/clients/contacts.js).
//
// The server's gates are clients.read to list and clients.update to change,
// so the controls follow `canUpdate` — the same flag the rest of the record
// uses for Edit.

import React, { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { post, put, del, serverSentence } from '../../shared/http';
import { DataTable, Notice, Pill, Sheet, FwButton, FwField, FwInput, FwSelect, FwTextarea, Skeleton } from '../../../ui/fw';
import { useOfficeQuery, Panel, FormError, FormRow, SheetFooter, ConfirmSheet, textOr, fmtDateTime } from './OfficePage';
import './ClientContacts.css';

export const PORTAL_STATE = {
  off: { label: 'No portal access', tone: 'neutral' },
  invited: { label: 'Invited', tone: 'sched' },
  active: { label: 'Portal active', tone: 'ok' },
  revoked: { label: 'Access turned off', tone: 'warn' },
};
export const ACCESS_LABEL = {
  billing: 'Invoices, payments and estimates',
  property: 'Estimates only — no money',
};

const EMPTY = { name: '', email: '', phone: '', access: 'billing', notes: '' };

function ContactSheet({ open, clientId, clientName, contact, portalReady, onClose, onDone }) {
  const qc = useQueryClient();
  // Mounted fresh per contact (the parent keys it), so the form starts from
  // the record every time it opens.
  const [form, setForm] = useState(() => (contact
    ? { name: contact.name || '', email: contact.email || '', phone: contact.phone || '', access: contact.access || 'billing', notes: contact.notes || '' }
    : EMPTY));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isNew = !contact;
  const key = contact ? contact.id : 'new';
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const refresh = () => qc.invalidateQueries({ queryKey: ['client-contacts', clientId] });

  const act = async (label, fn) => {
    setBusy(label); setError('');
    try {
      const r = await fn();
      await refresh();
      onDone(r);
      return true;
    } catch (e) {
      setError(serverSentence(e, 'That did not work. Please try again.'));
      // A 502 from Invite means access IS on and only the email failed —
      // the list must show that, so it is refreshed either way.
      await refresh();
      return false;
    } finally { setBusy(null); }
  };

  const save = (e) => {
    e.preventDefault();
    if (!form.email.trim()) { setError('An email address is required.'); return; }
    act('save', async () => (isNew
      ? post(`/clients/${clientId}/contacts`, form)
      : put(`/clients/${clientId}/contacts/${contact.id}`, form)));
  };
  const invite = () => act('invite', () => post(`/clients/${clientId}/contacts/${contact.id}/invite`, {}));
  const revoke = () => act('revoke', () => post(`/clients/${clientId}/contacts/${contact.id}/revoke`, {}));
  const remove = async () => {
    const ok = await act('delete', () => del(`/clients/${clientId}/contacts/${contact.id}`));
    if (ok) setConfirmDelete(false);
  };

  const formId = `of-contact-${key}`;
  const on = contact && (contact.portal === 'invited' || contact.portal === 'active');
  return (
    <>
      <Sheet
        open={open && !confirmDelete}
        title={isNew ? `Add a contact to ${clientName}` : (contact.name || contact.email)}
        onClose={onClose}
        footer={<SheetFooter onCancel={onClose} busy={busy === 'save'} submitLabel={isNew ? 'Add contact' : 'Save'} formId={formId} />}
      >
        <form id={formId} className="or-form" onSubmit={save} noValidate data-form="contact">
          <FormError error={error} />
          <FormRow>
            <FwField label="Name" htmlFor={`${formId}-name`}><FwInput id={`${formId}-name`} value={form.name} onChange={set('name')} autoComplete="off" /></FwField>
            <FwField label="Email" htmlFor={`${formId}-email`} required hint={!isNew && on ? 'Changing it turns portal access off until you invite the new address.' : undefined}>
              <FwInput id={`${formId}-email`} type="email" value={form.email} onChange={set('email')} autoComplete="off" />
            </FwField>
          </FormRow>
          <FormRow>
            <FwField label="Phone" htmlFor={`${formId}-phone`}><FwInput id={`${formId}-phone`} type="tel" value={form.phone} onChange={set('phone')} /></FwField>
            <FwField label="Can see" htmlFor={`${formId}-access`}>
              <FwSelect id={`${formId}-access`} value={form.access} onChange={set('access')}>
                <option value="billing">{ACCESS_LABEL.billing}</option>
                <option value="property">{ACCESS_LABEL.property}</option>
              </FwSelect>
            </FwField>
          </FormRow>
          <FwField label="Notes" htmlFor={`${formId}-notes`}><FwTextarea id={`${formId}-notes`} rows={2} value={form.notes} onChange={set('notes')} /></FwField>
        </form>

        {!isNew ? (
          <section className="or-contact__portal" data-section="portal">
            <h3 className="or-contact__h">Client portal</h3>
            <p className="or-note">
              <Pill tone={(PORTAL_STATE[contact.portal] || PORTAL_STATE.off).tone}>{(PORTAL_STATE[contact.portal] || PORTAL_STATE.off).label}</Pill>
              {contact.last_sign_in_at ? ` Last signed in ${fmtDateTime(contact.last_sign_in_at)}.` : ''}
              {contact.portal === 'active' && contact.signed_in_devices ? ` Signed in on ${contact.signed_in_devices} device${contact.signed_in_devices === 1 ? '' : 's'}.` : ''}
            </p>
            {!portalReady ? (
              <Notice tone="warn">The portal address (APP_URL) is not set on the server, so no sign-in link can be built and no invitation can be sent.</Notice>
            ) : null}
            <div className="or-actions">
              {portalReady ? (
                <FwButton variant={on ? 'ghost' : 'primary'} icon="mail" loading={busy === 'invite'} onClick={invite} data-action="invite-contact">
                  {on ? 'Send a new invitation' : contact.portal === 'revoked' ? 'Invite again' : 'Invite to the portal'}
                </FwButton>
              ) : null}
              {on ? <FwButton variant="ghost" icon="lock" loading={busy === 'revoke'} onClick={revoke} data-action="revoke-contact">Turn off portal access</FwButton> : null}
              <FwButton variant="stop" icon="trash" onClick={() => setConfirmDelete(true)} data-action="delete-contact">Remove contact</FwButton>
            </div>
            <p className="or-note">An invitation emails a sign-in link that works once, for 3 days. After that they sign in from the portal page with this email address — there is no password.</p>
          </section>
        ) : null}
      </Sheet>
      {!isNew ? (
        <ConfirmSheet open={open && confirmDelete} title={`Remove ${contact.name || contact.email}?`} confirmLabel="Remove contact" busy={busy === 'delete'} onConfirm={remove} onClose={() => setConfirmDelete(false)}>
          <p>{on ? 'Their portal access ends now; if this was their only client, every device they are signed in on is signed out.' : 'They are removed from this client.'}</p>
        </ConfirmSheet>
      ) : null}
    </>
  );
}

export default function ClientContacts({ client, canUpdate }) {
  const q = useOfficeQuery(['client-contacts', client.id], `/clients/${client.id}/contacts`);
  const contacts = useMemo(() => (q.data && q.data.contacts) || [], [q.data]);
  const portalReady = !!(q.data && q.data.portal_ready);
  const [sheet, setSheet] = useState(null); // { contact } | null
  const [flash, setFlash] = useState(null);
  const counted = !q.error && !q.isPending;

  const columns = useMemo(() => [
    { key: 'name', label: 'Name', role: 'title', render: (c) => <strong>{textOr(c.name || c.email)}</strong> },
    { key: 'email', label: 'Email', role: 'subtitle', render: (c) => c.email },
    { key: 'portal', label: 'Portal', role: 'badge', render: (c) => { const s = PORTAL_STATE[c.portal] || PORTAL_STATE.off; return <Pill tone={s.tone} data-portal={c.portal}>{s.label}</Pill>; } },
    { key: 'access', label: 'Can see', role: 'fact', render: (c) => ACCESS_LABEL[c.access] || c.access },
    { key: 'last', label: 'Last signed in', role: 'fact', render: (c) => (c.last_sign_in_at ? fmtDateTime(c.last_sign_in_at) : 'Never') },
    { key: 'phone', label: 'Phone', role: 'fact', render: (c) => textOr(c.phone) },
  ], []);

  return (
    <Panel
      className="or-panel--flush"
      title={counted ? `Contacts & portal access (${contacts.length})` : 'Contacts & portal access'}
      actions={canUpdate ? <FwButton variant="ghost" icon="plus" onClick={() => { setFlash(null); setSheet({ contact: null }); }} data-action="add-contact">Add contact</FwButton> : null}
    >
      {q.error ? <Notice tone="stop" className="or-error">{serverSentence(q.error, "Could not load this client's contacts.")}</Notice> : null}
      {flash ? <Notice tone="ok" data-note="contact-done">{flash}</Notice> : null}
      {q.isPending ? <Skeleton lines={2} /> : null}
      {!q.isPending ? (
        <DataTable
          dense
          columns={columns}
          rows={contacts}
          empty={q.error ? 'The contacts could not be loaded, so this is not a list of none.' : 'No contacts yet. Add one to invite them to the client portal, where they can see and pay their invoices.'}
          onRowClick={canUpdate ? (c) => { setFlash(null); setSheet({ contact: c }); } : undefined}
          caption={`Contacts for ${client.name}`}
        />
      ) : null}
      <ContactSheet
        key={sheet ? (sheet.contact ? `c${sheet.contact.id}` : 'new') : 'closed'}
        open={!!sheet}
        clientId={client.id}
        clientName={client.name}
        contact={sheet ? (contacts.find((c) => sheet.contact && c.id === sheet.contact.id) || sheet.contact) : null}
        portalReady={portalReady}
        onClose={() => setSheet(null)}
        onDone={(r) => {
          const added = r && r.contact && !(sheet && sheet.contact);
          setFlash((r && r.message) || (added ? `${r.contact.email} added. Invite them when they should have portal access.` : (r && r.ok ? 'Contact removed.' : 'Saved.')));
          setSheet(null);
        }}
      />
    </Panel>
  );
}
